import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, XOctagon, Plus, ExternalLink } from "lucide-react";
import { QUOTE_PRODUCTS } from "@/lib/crm";

export default function KoPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [search, setSearch] = useState("");
  const [fFrom, setFrom] = useState("");
  const [fTo, setTo] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [fOperator, setFOperator] = useState("");
  const [fReason, setFReason] = useState("");

  useEffect(() => {
    api.get("/ko-reasons").then(({ data }) => setReasons(data)).catch(() => {});
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (fProduct) params.product = fProduct;
      if (fOperator) params.operator_id = fOperator;
      if (fReason) params.reason = fReason;
      const { data } = await api.get("/ko", { params });
      setRows(data);
    } finally { setLoading(false); }
  }, [search, fFrom, fTo, fProduct, fOperator, fReason]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="ko-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><XOctagon className="h-6 w-6 text-danger" /> KO / Annullamenti</h1>
          <p className="mt-1 text-sm text-slate-500">Annullamenti tracciabili collegati alle pratiche. Non cancellano dati storici.</p>
        </div>
        <Button onClick={() => setDlg(true)} className="gap-1.5" data-testid="new-ko-btn"><Plus className="h-4 w-4" /> Registra KO</Button>
      </div>

      <div className="ds-card flex flex-wrap items-end gap-3 p-4" data-testid="ko-filters">
        <Input placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-[190px]" data-testid="ko-search" />
        <div className="space-y-1"><Label className="ds-label">Dal</Label><Input type="date" value={fFrom} onChange={(e) => setFrom(e.target.value)} data-testid="ko-filter-from" /></div>
        <div className="space-y-1"><Label className="ds-label">Al</Label><Input type="date" value={fTo} onChange={(e) => setTo(e.target.value)} data-testid="ko-filter-to" /></div>
        <Select value={fProduct || "all"} onValueChange={(v) => setFProduct(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="ko-filter-product"><SelectValue placeholder="Prodotto" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i prodotti</SelectItem>{QUOTE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fReason || "all"} onValueChange={(v) => setFReason(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="ko-filter-reason"><SelectValue placeholder="Motivo" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i motivi</SelectItem>{reasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && (
          <Select value={fOperator || "all"} onValueChange={(v) => setFOperator(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="ko-filter-operator"><SelectValue placeholder="Agente" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : rows.length === 0 ? <div className="ds-card py-12 text-center text-sm text-slate-500" data-testid="ko-empty">Nessun KO trovato con i filtri correnti.</div>
        : (
          <div className="ds-card ds-table-scroll p-0" data-testid="ko-table">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Data</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Agente</th><th className="px-4 py-3">Motivo</th><th className="px-4 py-3">Note</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((k) => (
                  <tr key={k.id} className="hover:bg-danger/5" data-testid={`ko-row-${k.id}`}>
                    <td className="px-4 py-3">{k.ko_date}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{k.customer_name || "—"}</td>
                    <td className="px-4 py-3">{k.product || "—"}</td>
                    <td className="px-4 py-3">{k.operator_name || "—"}</td>
                    <td className="px-4 py-3">{k.reason || "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{k.notes || "—"}</td>
                    <td className="px-4 py-3"><Button size="icon" variant="ghost" title="Apri cliente" onClick={() => navigate(`/clienti/${k.customer_id}`)} data-testid={`ko-open-${k.id}`}><ExternalLink className="h-4 w-4" /></Button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {dlg && <KoDialog open={dlg} onOpenChange={setDlg} onSaved={load} />}
    </div>
  );
}

function KoDialog({ open, onOpenChange, onSaved }) {
  const [prods, setProds] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [pid, setPid] = useState("");
  const [reason, setReason] = useState("");
  const [koDate, setKoDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/production").then(({ data }) => setProds(data.filter((p) => p.status !== "KO"))).catch(() => {});
    api.get("/ko-reasons").then(({ data }) => setReasons(data)).catch(() => {});
  }, []);

  const save = async () => {
    if (!pid) { setError("Seleziona la pratica."); return; }
    setSaving(true); setError("");
    try {
      await api.post("/ko", { production_id: pid, reason: reason || null, ko_date: koDate || null, notes: notes || null });
      toast.success("KO registrato"); onSaved(); onOpenChange(false);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-md" data-testid="ko-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display"><XOctagon className="h-5 w-5 text-danger" /> Registra KO</DialogTitle>
          <DialogDescription>La pratica passa a stato KO. Cliente, opportunità, preventivo e produzione restano nello storico (nessuna cancellazione).</DialogDescription></DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="ko-error">{error}</div>}
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Pratica *</Label>
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="ko-prod-select"><SelectValue placeholder="Seleziona pratica" /></SelectTrigger>
              <SelectContent className="bg-white">{prods.map((p) => <SelectItem key={p.id} value={p.id}>{p.production_number} · {p.customer_name} · {p.product}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Motivo</Label>
              <Select value={reason || "none"} onValueChange={(v) => setReason(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="ko-reason"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="none">—</SelectItem>{reasons.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Data KO</Label><Input type="date" value={koDate} onChange={(e) => setKoDate(e.target.value)} data-testid="ko-date" /></div>
          </div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ko-notes" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={save} disabled={saving} className="bg-danger hover:bg-danger/90" data-testid="ko-confirm">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registra KO"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
